// Shared download P&L grading + summary helpers.
// Mirrors the grading used in QuickPick, BetMaker and the Admin Downloads tab.

export const normalizeCombo = (combo) =>
  String(combo || '')
    .replace(/\s+/g, '')
    .split(/[,/-]/)
    .filter(Boolean)
    .map((x) => x.trim())
    .join('-');

const POOL_ALIASES = {
  ex: 'exacta', exacta: 'exacta', tri: 'trifecta', trifecta: 'trifecta',
  spr: 'superfecta', superfecta: 'superfecta', dbl: 'daily double', 'daily double': 'daily double',
  p3: 'pick 3', 'pick 3': 'pick 3', p4: 'pick 4', 'pick 4': 'pick 4', p5: 'pick 5', 'pick 5': 'pick 5',
  p6: 'pick 6', 'pick 6': 'pick 6', win: 'win', plc: 'place', place: 'place', wp: 'win',
};

export const normPool = (p) =>
  POOL_ALIASES[String(p || '').toLowerCase().trim()] || String(p || '').toLowerCase().trim();

// Grade one ticket {pool, combo, amount} against the race payouts. Returns $ returned.
export const gradeTicket = (t, payouts) => {
  const combo = String(t.combo || '');
  let poolName, winnerKey;
  if (/^WIN/i.test(combo)) { poolName = 'win'; winnerKey = combo.split(',')[1]; }
  else if (/^(PLC|PLACE)/i.test(combo)) { poolName = 'place'; winnerKey = combo.split(',')[1]; }
  else { poolName = normPool(t.pool); winnerKey = combo; }
  const nWinner = normalizeCombo(winnerKey);
  const m = (payouts || []).find(
    (p) => normPool(p.pool) === poolName && normalizeCombo(p.winners) === nWinner
  );
  return m ? (t.amount || 0) * (m.payout || 0) : 0;
};

// Format a stored download timestamp in the viewer's LOCAL timezone.
export const formatLocalDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });
};

// Group key + label for a download under the chosen summary mode.
const groupKeyLabel = (d, mode) => {
  const pool = normPool(d.pool) || '—';
  const track = (d.track || '—').toUpperCase();
  if (mode === 'track') return { key: track, label: track };
  if (mode === 'trackpool') return { key: `${track}|${pool}`, label: `${track} · ${pool}` };
  return { key: pool, label: pool }; // default: by pool
};

// Build a graded summary grouped by pool / track / track+pool.
// `payoutsByRace` maps `${track}|${race_number}|${race_date}` -> payouts[].
// Returns { rows: [{key,label,total,wagered,returned,gradedWagered,graded,roi}], ungraded }.
export const groupSummary = (downloads, mode, payoutsByRace) => {
  const groups = {};
  let ungraded = 0;
  (downloads || []).forEach((d) => {
    const { key, label } = groupKeyLabel(d, mode);
    const g = groups[key] || (groups[key] = {
      key, label, total: 0, wagered: 0, returned: 0, gradedWagered: 0, graded: 0,
    });
    g.total += 1;
    g.wagered += d.total_wager || 0;
    const raceKey = `${d.track}|${d.race_number}|${d.race_date}`;
    const payouts = (d.race_date && (d.tickets || []).length)
      ? (payoutsByRace[raceKey] || [])
      : [];
    if (payouts.length > 0) {
      g.gradedWagered += (d.tickets || []).reduce((s, t) => s + (t.amount || 0), 0);
      g.returned += (d.tickets || []).reduce((s, t) => s + gradeTicket(t, payouts), 0);
      g.graded += 1;
    } else {
      ungraded += 1;
    }
  });
  const rows = Object.values(groups)
    .map((g) => ({
      ...g,
      roi: g.gradedWagered > 0 ? ((g.returned - g.gradedWagered) / g.gradedWagered) * 100 : 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return { rows, ungraded };
};

// Given a list of downloads (each with race_date 'YYYY-MM-DD'), return the subset
// whose race_date falls within the most recent 7 days present in the data.
// `cutoff` (exclusive-inclusive boundary) is returned so the UI can show an info bar.
export const capToRecent7Days = (downloads) => {
  const dated = (downloads || []).filter((d) => d.race_date);
  if (!dated.length) return { rows: downloads || [], capped: false, cutoff: null, maxDate: null };
  const maxDate = dated.reduce((m, d) => (d.race_date > m ? d.race_date : m), dated[0].race_date);
  const minDate = dated.reduce((m, d) => (d.race_date < m ? d.race_date : m), dated[0].race_date);
  // cutoff = maxDate - 6 days (inclusive window of 7 calendar days)
  const end = new Date(`${maxDate}T00:00:00`);
  end.setDate(end.getDate() - 6);
  const cutoff = end.toISOString().slice(0, 10);
  const spanDays = Math.round((new Date(`${maxDate}T00:00:00`) - new Date(`${minDate}T00:00:00`)) / 86400000) + 1;
  if (spanDays <= 7) return { rows: downloads || [], capped: false, cutoff, maxDate };
  const rows = (downloads || []).filter((d) => !d.race_date || d.race_date >= cutoff);
  return { rows, capped: true, cutoff, maxDate };
};
